import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Stack,
  TextField,
  Typography,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ProgressEstimationService from '../services/progressEstimationService';
import { ApiError } from '../services/api';
import type {
  CreateProgressEstimationInput,
  ProgressEstimation as ProgressEstimationType,
  ProgressEstimationAccuracyResponse,
} from '../services/types';

const metricOptions = [
  { value: 'weight', label: 'Weight' },
  { value: 'reps', label: 'Reps' },
  { value: 'body_fat', label: 'Body Fat' },
] as const;

const formatDiff = (value: number | null) => {
  if (value === null) return '-';
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}`;
};

const getErrorMessage = (error: unknown, messages: Partial<Record<number, string>>, fallback: string) => {
  if (error instanceof ApiError) {
    return messages[error.status] ?? fallback;
  }

  return fallback;
};

const ProgressEstimation: React.FC = () => {
  const [estimationsLoading, setEstimationsLoading] = useState(false);
  const [accuracyLoading, setAccuracyLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState<{ severity: 'error' | 'info'; text: string } | null>(null);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const [estimations, setEstimations] = useState<ProgressEstimationType[]>([]);

  const [metricType, setMetricType] = useState<'weight' | 'reps' | 'body_fat'>('weight');
  const [targetDate, setTargetDate] = useState('');
  const [targetValue, setTargetValue] = useState('');

  const [windowDays, setWindowDays] = useState<30 | 90>(30);
  const [accuracy, setAccuracy] = useState<ProgressEstimationAccuracyResponse | null>(null);
  const isBusy = estimationsLoading || accuracyLoading || generating;

  const loadEstimations = async () => {
    setEstimationsLoading(true);
    try {
      const data = await ProgressEstimationService.getEstimations();
      setFeatureDisabled(false);
      setEstimations(data);
    } catch (error) {
      const nextMessage = getErrorMessage(
        error,
        {
          403: 'Нямаш достъп до прогнозите за твоя профил.',
          404: 'Функционалността за прогнози е изключена в момента.',
          422: 'Заявката за прогнози е невалидна.',
          429: 'Има твърде много заявки. Опитай отново след малко.',
        },
        'Неуспешно зареждане на прогнозите.'
      );

      if (error instanceof ApiError && error.status === 404) {
        setFeatureDisabled(true);
      }

      setMessage({ severity: 'error', text: nextMessage });
    } finally {
      setEstimationsLoading(false);
    }
  };

  const loadAccuracy = async (selectedWindow: 30 | 90) => {
    setAccuracyLoading(true);
    try {
      const data = await ProgressEstimationService.getAccuracy(selectedWindow);
      setFeatureDisabled(false);
      setAccuracy(data);
    } catch (error) {
      const nextMessage = getErrorMessage(
        error,
        {
          404: 'Функционалността за прогнози е изключена в момента.',
          422: 'Невалиден период за точност.',
          429: 'Има твърде много заявки. Опитай отново след малко.',
        },
        'Неуспешно зареждане на точността на прогнозите.'
      );

      if (error instanceof ApiError && error.status === 404) {
        setFeatureDisabled(true);
      }

      setMessage({ severity: 'error', text: nextMessage });
    } finally {
      setAccuracyLoading(false);
    }
  };

  useEffect(() => {
    setMessage(null);
    void loadEstimations();
  }, []);

  useEffect(() => {
    void loadAccuracy(windowDays);
  }, [windowDays]);

  const handleGenerate = async () => {
    const payload: CreateProgressEstimationInput = {
      metric_type: metricType,
      target_date: targetDate,
      target_value: targetValue ? Number(targetValue) : undefined,
    };

    setGenerating(true);
    setMessage(null);
    try {
      await ProgressEstimationService.generate(payload);
      await loadEstimations();
      await loadAccuracy(windowDays);
    } catch (error) {
      const nextMessage = getErrorMessage(
        error,
        {
          404: 'Функционалността за прогнози е изключена в момента.',
          422: 'Невалидни данни или недостатъчна история за прогноза.',
          429: 'Лимитът за генериране е достигнат. Опитай отново след малко.',
        },
        'Неуспешно генериране на прогноза.'
      );

      if (error instanceof ApiError && error.status === 404) {
        setFeatureDisabled(true);
      }

      setMessage({ severity: 'error', text: nextMessage });
    } finally {
      setGenerating(false);
    }
  };

  const handleWindowChange = (value: 30 | 90) => {
    setWindowDays(value);
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={700}>Progress Estimation</Typography>
        <Typography variant="body2" color="text.secondary">
          Current values, predictions by date, and target tracking status.
        </Typography>
      </Box>

      {message && <Alert severity={message.severity}>{message.text}</Alert>}

      {featureDisabled && (
        <Alert severity="info">
          Функционалността за Progress Estimation е изключена в момента.
        </Alert>
      )}

      {!featureDisabled && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Generate Personal Estimation</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="metric-type-label">Metric</InputLabel>
                  <Select
                    labelId="metric-type-label"
                    label="Metric"
                    value={metricType}
                    onChange={(e) => setMetricType(e.target.value as 'weight' | 'reps' | 'body_fat')}
                  >
                    {metricOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Target Date"
                  type="date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Target Value"
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleGenerate} disabled={isBusy || !targetDate}>
                Generate
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setMessage(null);
                  void loadEstimations();
                  void loadAccuracy(windowDays);
                }}
                disabled={isBusy}
              >
                Refresh My Data
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {isBusy && !featureDisabled && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!featureDisabled && (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {estimations.length > 0 ? `My Estimations (${estimations.length})` : 'My Estimations'}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => void loadEstimations()}
              disabled={isBusy}
              size="small"
            >
              Refresh
            </Button>
          </Stack>

          {estimations.length === 0 && !estimationsLoading ? (
            <Alert severity="info">Няма активни прогнози за твоя профил.</Alert>
          ) : (
            <Grid container spacing={2}>
              {estimations.map((estimation) => (
                <Grid key={estimation.id} size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="h6">{estimation.metric_type}</Typography>
                          <Chip
                            label={estimation.status}
                            color={estimation.status === 'on-track' ? 'success' : estimation.status === 'off-track' ? 'warning' : 'default'}
                          />
                        </Stack>
                        <Typography variant="body2">Current: {estimation.current_value}</Typography>
                        <Typography variant="body2">Predicted: {estimation.predicted_value} (by {estimation.target_date})</Typography>
                        <Typography variant="body2">Target: {estimation.target_value ?? '-'}</Typography>
                        <Typography variant="body2">Delta to target: {formatDiff(estimation.difference_to_target)}</Typography>
                        <Typography variant="caption" color="text.secondary">Model: {estimation.model_version}</Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {!featureDisabled && (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button variant={windowDays === 30 ? 'contained' : 'outlined'} onClick={() => handleWindowChange(30)} disabled={isBusy}>30 Days</Button>
              <Button variant={windowDays === 90 ? 'contained' : 'outlined'} onClick={() => handleWindowChange(90)} disabled={isBusy}>90 Days</Button>
            </Stack>

            <Typography variant="body2" sx={{ mb: 2 }}>
              Avg MAE: {accuracy?.aggregate.avg_mae ?? '-'} | Avg MAPE: {accuracy?.aggregate.avg_mape ?? '-'} | Samples: {accuracy?.aggregate.samples ?? 0}
            </Typography>

            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={accuracy?.timeline ?? []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="avg_mae" fill="#ffa726" name="Avg MAE" />
                  <Bar dataKey="avg_mape" fill="#29b6f6" name="Avg MAPE" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default ProgressEstimation;
